import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import HousingMatchModal from "./HousingMatchModal";

const HOUSING_TYPE_LABELS = {
  habitant: "Chez l'habitant",
  hotel: "Hôtel",
  gite: "Gîte",
  congregation: "Congrégation religieuse",
};

const HOUSING_TYPE_COLOR = {
  habitant: "var(--moss)",
  hotel: "var(--lapis)",
  gite: "var(--gold)",
  congregation: "var(--cinnabar)",
};

// ============================================================ KANBAN
function TeamsKanban({ locale }) {
  const [data, setData] = useState(null);
  const reload = useCallback(async () => {
    const r = await api.get("/admin/teams");
    setData(r.data);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  if (!data) return <div className="text-center py-12 font-body text-sm" style={{ color: "var(--muted)" }}>…</div>;

  const renderCategory = (catKey) => {
    const col = data.columns[catKey];
    const keys = Object.keys(col);
    const totalMembers = Object.values(col).reduce((sum, arr) => sum + arr.length, 0);
    return (
      <section key={catKey} className="space-y-3">
        <div className="flex items-baseline justify-between border-b pb-2" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-display text-2xl">{data.category_labels[catKey]}</h3>
          <span className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>
            {totalMembers} {locale === "fr" ? "membres" : "members"}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {keys.map((funcKey) => {
            const members = col[funcKey];
            const isUnassigned = funcKey === "_unassigned";
            const title = isUnassigned
              ? (locale === "fr" ? "Sans rôle assigné" : "No role assigned")
              : (data.labels[funcKey] || funcKey);
            return (
              <article key={funcKey} data-testid={`team-col-${catKey}-${funcKey}`}
                       className="border bg-white p-4 flex flex-col"
                       style={{
                         borderColor: "var(--border)",
                         borderTopWidth: 3,
                         borderTopColor: isUnassigned ? "var(--muted)" : "var(--cinnabar)",
                         minHeight: 160,
                       }}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display text-base" style={{ color: isUnassigned ? "var(--muted)" : "var(--ink)" }}>
                    {title}
                  </h4>
                  <span className="font-caps text-[0.6rem] px-2 py-0.5"
                        style={{ background: "var(--parchment-2)", color: "var(--ink-2)" }}>
                    {members.length}
                  </span>
                </div>
                {members.length === 0 ? (
                  <div className="font-body text-xs italic" style={{ color: "var(--muted)" }}>
                    {locale === "fr" ? "Aucun membre" : "No member"}
                  </div>
                ) : (
                  <ul className="space-y-1.5 text-sm font-body">
                    {members.map((m) => (
                      <li key={m.id} className="flex items-center justify-between gap-2">
                        <span style={{ color: m.active === false ? "var(--muted)" : "var(--ink)" }}>
                          {m.name || m.email}
                        </span>
                        {m.city && <span className="font-body text-xs" style={{ color: "var(--muted)" }}>{m.city}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </article>
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div data-testid="teams-kanban" className="space-y-10">
      {renderCategory("organisateur")}
      {renderCategory("artiste")}
    </div>
  );
}

// ============================================================ HOUSING
function HousingPanel({ locale }) {
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [matchRequestId, setMatchRequestId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([
        api.get("/admin/housing/offers"),
        api.get("/admin/housing/requests"),
      ]);
      setOffers(o.data || []);
      setRequests(r.data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const offerById = (id) => offers.find((o) => o.id === id);
  const totalOffered = offers.reduce((s, o) => s + (o.capacity || 0), 0);
  const totalAssigned = requests.filter((r) => r.status === "assigned").reduce((s, r) => s + (r.nb_people || 1), 0);
  const totalRequested = requests.reduce((s, r) => s + (r.nb_people || 1), 0);

  return (
    <div data-testid="housing-panel" className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={locale === "fr" ? "Places offertes" : "Beds offered"} value={totalOffered} />
        <Stat label={locale === "fr" ? "Places disponibles" : "Available"} value={Math.max(0, totalOffered - totalAssigned)} />
        <Stat label={locale === "fr" ? "Demandes" : "Requests"} value={totalRequested} />
        <Stat label={locale === "fr" ? "Affectées" : "Assigned"} value={totalAssigned} />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <button data-testid="housing-new-offer" onClick={() => setShowOfferForm((v) => !v)} className="btn btn-primary" style={{ fontSize: "0.7rem", padding: "0.55rem 1rem" }}>
          {showOfferForm ? "—" : "+"} {locale === "fr" ? "Nouvelle offre" : "New offer"}
        </button>
        <button data-testid="housing-new-request" onClick={() => setShowRequestForm((v) => !v)} className="btn btn-outline" style={{ fontSize: "0.7rem", padding: "0.55rem 1rem" }}>
          {showRequestForm ? "—" : "+"} {locale === "fr" ? "Nouvelle demande" : "New request"}
        </button>
      </div>

      {showOfferForm && <HousingOfferForm onCreated={() => { reload(); setShowOfferForm(false); }} locale={locale} />}
      {showRequestForm && <HousingRequestForm onCreated={() => { reload(); setShowRequestForm(false); }} locale={locale} />}

      {/* Offers */}
      <div className="admin-card">
        <div className="font-display text-xl mb-3">{locale === "fr" ? "Offres d'hébergement" : "Housing offers"}</div>
        {loading ? <div className="text-center py-6 font-body text-sm" style={{ color: "var(--muted)" }}>…</div>
         : offers.length === 0 ? <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>{locale === "fr" ? "Aucune offre." : "No offers."}</div>
         : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead><tr className="border-b" style={{ borderColor: "var(--border)" }}>
                <Th>{locale === "fr" ? "TYPE" : "TYPE"}</Th>
                <Th>{locale === "fr" ? "HÔTE" : "HOST"}</Th>
                <Th>{locale === "fr" ? "VILLE" : "CITY"}</Th>
                <Th>{locale === "fr" ? "CAPACITÉ" : "CAPACITY"}</Th>
                <Th>{locale === "fr" ? "RESTANT" : "REMAIN"}</Th>
                <Th>{locale === "fr" ? "CONTACT" : "CONTACT"}</Th>
                <Th align="right">{locale === "fr" ? "ACTIONS" : "ACTIONS"}</Th>
              </tr></thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id} data-testid={`housing-offer-${o.id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 px-3">
                      <span className="inline-block px-2 py-0.5 font-caps text-[0.55rem]"
                            style={{ background: "rgba(0,0,0,0.05)", color: HOUSING_TYPE_COLOR[o.type], letterSpacing: "0.18em" }}>
                        {HOUSING_TYPE_LABELS[o.type] || o.type}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-display">{o.host_name}</td>
                    <td className="py-2 px-3">{o.city}</td>
                    <td className="py-2 px-3">{o.capacity}</td>
                    <td className="py-2 px-3" style={{ color: (o.remaining_capacity || 0) === 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                      <strong>{o.remaining_capacity}</strong>
                    </td>
                    <td className="py-2 px-3 text-xs" style={{ color: "var(--muted)" }}>{o.phone || o.email || "—"}</td>
                    <td className="py-2 px-3 text-right">
                      <button data-testid={`housing-offer-delete-${o.id}`}
                              onClick={async () => { if (window.confirm("Supprimer cette offre ?")) { await api.delete(`/admin/housing/offers/${o.id}`); reload(); } }}
                              className="font-caps text-[0.55rem] px-2 py-1 border"
                              style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
                        {locale === "fr" ? "SUPPR." : "DEL."}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Requests */}
      <div className="admin-card">
        <div className="font-display text-xl mb-3">{locale === "fr" ? "Demandes d'hébergement" : "Housing requests"}</div>
        {loading ? <div className="text-center py-6 font-body text-sm" style={{ color: "var(--muted)" }}>…</div>
         : requests.length === 0 ? <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>{locale === "fr" ? "Aucune demande." : "No requests."}</div>
         : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead><tr className="border-b" style={{ borderColor: "var(--border)" }}>
                <Th>{locale === "fr" ? "INVITÉ" : "GUEST"}</Th>
                <Th>{locale === "fr" ? "PERS." : "PPL"}</Th>
                <Th>{locale === "fr" ? "DATES" : "DATES"}</Th>
                <Th>{locale === "fr" ? "PRÉFÉRENCE" : "PREF"}</Th>
                <Th>{locale === "fr" ? "AFFECTATION" : "ASSIGNMENT"}</Th>
                <Th align="right">{locale === "fr" ? "ACTIONS" : "ACTIONS"}</Th>
              </tr></thead>
              <tbody>
                {requests.map((r) => {
                  const assigned = r.assigned_offer_id ? offerById(r.assigned_offer_id) : null;
                  return (
                    <tr key={r.id} data-testid={`housing-request-${r.id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 px-3">
                        <div className="font-display">{r.guest_name}</div>
                        <div className="text-xs" style={{ color: "var(--muted)" }}>{r.email}</div>
                      </td>
                      <td className="py-2 px-3">{r.nb_people}</td>
                      <td className="py-2 px-3 text-xs">{r.arrival} → {r.departure}</td>
                      <td className="py-2 px-3 text-xs" style={{ color: "var(--muted)" }}>
                        {HOUSING_TYPE_LABELS[r.preference] || r.preference || "—"}
                      </td>
                      <td className="py-2 px-3">
                        <select data-testid={`housing-assign-${r.id}`}
                                value={r.assigned_offer_id || ""}
                                onChange={async (e) => {
                                  if (e.target.value) {
                                    await api.post(`/admin/housing/requests/${r.id}/assign`, { offer_id: e.target.value });
                                  } else {
                                    await api.post(`/admin/housing/requests/${r.id}/unassign`);
                                  }
                                  reload();
                                }}
                                className="font-body text-xs px-2 py-1 border" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
                          <option value="">— {locale === "fr" ? "Non affecté" : "Unassigned"}</option>
                          {offers.map((o) => {
                            const fits = (o.remaining_capacity || 0) >= (r.nb_people || 1) || o.id === r.assigned_offer_id;
                            return (
                              <option key={o.id} value={o.id} disabled={!fits}>
                                {o.host_name} ({HOUSING_TYPE_LABELS[o.type]}, {o.city}) — {o.remaining_capacity}/{o.capacity}
                              </option>
                            );
                          })}
                        </select>
                        {assigned && <div className="text-xs mt-1" style={{ color: "var(--moss)" }}>✓ {assigned.host_name}</div>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {!r.assigned_offer_id && (
                          <button data-testid={`housing-match-${r.id}`}
                                  onClick={() => setMatchRequestId(r.id)}
                                  className="font-caps text-[0.55rem] px-2 py-1 border mr-1"
                                  style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>
                            ◯ {locale === "fr" ? "MATCH" : "MATCH"}
                          </button>
                        )}
                        <button data-testid={`housing-request-delete-${r.id}`}
                                onClick={async () => { if (window.confirm("Supprimer cette demande ?")) { await api.delete(`/admin/housing/requests/${r.id}`); reload(); } }}
                                className="font-caps text-[0.55rem] px-2 py-1 border"
                                style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
                          {locale === "fr" ? "SUPPR." : "DEL."}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {matchRequestId && (
        <HousingMatchModal requestId={matchRequestId} locale={locale}
                            onClose={() => setMatchRequestId(null)}
                            onAssigned={() => { setMatchRequestId(null); reload(); }} />
      )}
    </div>
  );
}

function HousingOfferForm({ onCreated, locale }) {
  const [f, setF] = useState({ type: "habitant", host_name: "", city: "", address: "", phone: "", email: "", capacity: 1, available_from: "", available_to: "", price_note: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/housing/offers", { ...f, capacity: parseInt(f.capacity, 10) || 1 });
    onCreated?.();
  };
  return (
    <form onSubmit={submit} data-testid="housing-offer-form" className="admin-card space-y-3">
      <div className="font-display text-lg">{locale === "fr" ? "Nouvelle offre d'hébergement" : "New housing offer"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="input-classic">
          <option value="habitant">{HOUSING_TYPE_LABELS.habitant}</option>
          <option value="hotel">{HOUSING_TYPE_LABELS.hotel}</option>
          <option value="gite">{HOUSING_TYPE_LABELS.gite}</option>
          <option value="congregation">{HOUSING_TYPE_LABELS.congregation}</option>
        </select>
        <input required placeholder={locale === "fr" ? "Hôte / Établissement" : "Host / Venue"} value={f.host_name} onChange={(e) => setF({ ...f, host_name: e.target.value })} className="input-classic" />
        <input required placeholder={locale === "fr" ? "Ville" : "City"} value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Adresse" : "Address"} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="input-classic md:col-span-2" />
        <input type="number" min="1" placeholder={locale === "fr" ? "Capacité (lits)" : "Capacity (beds)"} value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Téléphone" : "Phone"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-classic" />
        <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Tarif (optionnel)" : "Price (optional)"} value={f.price_note} onChange={(e) => setF({ ...f, price_note: e.target.value })} className="input-classic" />
        <input type="date" placeholder="Disponible du" value={f.available_from} onChange={(e) => setF({ ...f, available_from: e.target.value })} className="input-classic" />
        <input type="date" placeholder="au" value={f.available_to} onChange={(e) => setF({ ...f, available_to: e.target.value })} className="input-classic" />
        <textarea placeholder={locale === "fr" ? "Notes" : "Notes"} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input-classic md:col-span-3" />
      </div>
      <button type="submit" data-testid="housing-offer-submit" className="btn btn-primary" style={{ fontSize: "0.7rem" }}>
        {locale === "fr" ? "Enregistrer" : "Save"}
      </button>
    </form>
  );
}

function HousingRequestForm({ onCreated, locale }) {
  const [f, setF] = useState({ guest_name: "", email: "", phone: "", nb_people: 1, arrival: "", departure: "", preference: "indifferent", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/housing/requests", { ...f, nb_people: parseInt(f.nb_people, 10) || 1 });
    onCreated?.();
  };
  return (
    <form onSubmit={submit} data-testid="housing-request-form" className="admin-card space-y-3">
      <div className="font-display text-lg">{locale === "fr" ? "Nouvelle demande d'hébergement" : "New housing request"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input required placeholder={locale === "fr" ? "Nom de l'invité" : "Guest name"} value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} className="input-classic" />
        <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Téléphone" : "Phone"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-classic" />
        <input type="number" min="1" placeholder={locale === "fr" ? "Personnes" : "People"} value={f.nb_people} onChange={(e) => setF({ ...f, nb_people: e.target.value })} className="input-classic" />
        <input type="date" placeholder="Arrivée" value={f.arrival} onChange={(e) => setF({ ...f, arrival: e.target.value })} className="input-classic" />
        <input type="date" placeholder="Départ" value={f.departure} onChange={(e) => setF({ ...f, departure: e.target.value })} className="input-classic" />
        <select value={f.preference} onChange={(e) => setF({ ...f, preference: e.target.value })} className="input-classic">
          <option value="indifferent">{locale === "fr" ? "Indifférent" : "Any"}</option>
          <option value="habitant">{HOUSING_TYPE_LABELS.habitant}</option>
          <option value="hotel">{HOUSING_TYPE_LABELS.hotel}</option>
          <option value="gite">{HOUSING_TYPE_LABELS.gite}</option>
          <option value="congregation">{HOUSING_TYPE_LABELS.congregation}</option>
        </select>
        <textarea placeholder={locale === "fr" ? "Notes" : "Notes"} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input-classic md:col-span-3" />
      </div>
      <button type="submit" data-testid="housing-request-submit" className="btn btn-primary" style={{ fontSize: "0.7rem" }}>
        {locale === "fr" ? "Enregistrer" : "Save"}
      </button>
    </form>
  );
}

// ============================================================ TRANSPORT
function TransportPanel({ locale }) {
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);

  const reload = useCallback(async () => {
    const [o, r] = await Promise.all([
      api.get("/admin/transport/offers"),
      api.get("/admin/transport/requests"),
    ]);
    setOffers(o.data || []); setRequests(r.data || []);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const offerById = (id) => offers.find((o) => o.id === id);
  const totalSeats = offers.reduce((s, o) => s + (o.seats || 0), 0);
  const totalAssigned = requests.filter((r) => r.status === "assigned").reduce((s, r) => s + (r.nb_people || 1), 0);

  return (
    <div data-testid="transport-panel" className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={locale === "fr" ? "Places véhicules" : "Vehicle seats"} value={totalSeats} />
        <Stat label={locale === "fr" ? "Disponibles" : "Available"} value={Math.max(0, totalSeats - totalAssigned)} />
        <Stat label={locale === "fr" ? "Demandes" : "Requests"} value={requests.length} />
        <Stat label={locale === "fr" ? "Affectées" : "Assigned"} value={totalAssigned} />
      </div>

      <div className="flex flex-wrap gap-2">
        <button data-testid="transport-new-offer" onClick={() => setShowOfferForm((v) => !v)} className="btn btn-primary" style={{ fontSize: "0.7rem", padding: "0.55rem 1rem" }}>
          {showOfferForm ? "—" : "+"} {locale === "fr" ? "Nouveau trajet (offre)" : "New ride (offer)"}
        </button>
        <button data-testid="transport-new-request" onClick={() => setShowRequestForm((v) => !v)} className="btn btn-outline" style={{ fontSize: "0.7rem", padding: "0.55rem 1rem" }}>
          {showRequestForm ? "—" : "+"} {locale === "fr" ? "Nouvelle demande" : "New request"}
        </button>
      </div>

      {showOfferForm && <TransportOfferForm onCreated={() => { reload(); setShowOfferForm(false); }} locale={locale} />}
      {showRequestForm && <TransportRequestForm onCreated={() => { reload(); setShowRequestForm(false); }} locale={locale} />}

      <div className="admin-card">
        <div className="font-display text-xl mb-3">{locale === "fr" ? "Trajets proposés" : "Ride offers"}</div>
        {offers.length === 0 ? <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>{locale === "fr" ? "Aucun trajet." : "No ride."}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead><tr className="border-b" style={{ borderColor: "var(--border)" }}>
                <Th>{locale === "fr" ? "CONDUCTEUR" : "DRIVER"}</Th>
                <Th>{locale === "fr" ? "TRAJET" : "ROUTE"}</Th>
                <Th>{locale === "fr" ? "DATE" : "DATE"}</Th>
                <Th>{locale === "fr" ? "PLACES" : "SEATS"}</Th>
                <Th>{locale === "fr" ? "RESTANT" : "REMAIN"}</Th>
                <Th align="right">{locale === "fr" ? "ACTIONS" : "ACTIONS"}</Th>
              </tr></thead>
              <tbody>
                {offers.map((o) => (
                  <tr key={o.id} data-testid={`transport-offer-${o.id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 px-3">
                      <div className="font-display">{o.driver_name}</div>
                      <div className="text-xs" style={{ color: "var(--muted)" }}>{o.phone || o.email}</div>
                    </td>
                    <td className="py-2 px-3 text-sm">{o.origin} → {o.destination}</td>
                    <td className="py-2 px-3 text-xs">{o.date}</td>
                    <td className="py-2 px-3">{o.seats}</td>
                    <td className="py-2 px-3" style={{ color: (o.remaining_seats || 0) === 0 ? "var(--cinnabar)" : "var(--moss)" }}>
                      <strong>{o.remaining_seats}</strong>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <button onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/transport/offers/${o.id}`); reload(); } }}
                              className="font-caps text-[0.55rem] px-2 py-1 border"
                              style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
                        {locale === "fr" ? "SUPPR." : "DEL."}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-card">
        <div className="font-display text-xl mb-3">{locale === "fr" ? "Demandes de transport" : "Transport requests"}</div>
        {requests.length === 0 ? <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>{locale === "fr" ? "Aucune demande." : "No requests."}</div> : (
          <div className="overflow-x-auto">
            <table className="w-full font-body text-sm">
              <thead><tr className="border-b" style={{ borderColor: "var(--border)" }}>
                <Th>{locale === "fr" ? "DEMANDEUR" : "REQUESTER"}</Th>
                <Th>{locale === "fr" ? "DEPUIS" : "FROM"}</Th>
                <Th>{locale === "fr" ? "DATE" : "DATE"}</Th>
                <Th>{locale === "fr" ? "PERS." : "PPL"}</Th>
                <Th>{locale === "fr" ? "AFFECTATION" : "ASSIGNMENT"}</Th>
                <Th align="right">{locale === "fr" ? "ACTIONS" : "ACTIONS"}</Th>
              </tr></thead>
              <tbody>
                {requests.map((r) => {
                  const assigned = r.assigned_offer_id ? offerById(r.assigned_offer_id) : null;
                  return (
                    <tr key={r.id} data-testid={`transport-request-${r.id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 px-3">
                        <div className="font-display">{r.guest_name}</div>
                        <div className="text-xs" style={{ color: "var(--muted)" }}>{r.phone || r.email}</div>
                      </td>
                      <td className="py-2 px-3 text-sm">{r.origin}</td>
                      <td className="py-2 px-3 text-xs">{r.date}</td>
                      <td className="py-2 px-3">{r.nb_people}</td>
                      <td className="py-2 px-3">
                        <select data-testid={`transport-assign-${r.id}`}
                                value={r.assigned_offer_id || ""}
                                onChange={async (e) => {
                                  if (e.target.value) {
                                    await api.post(`/admin/transport/requests/${r.id}/assign`, { offer_id: e.target.value });
                                  } else {
                                    await api.post(`/admin/transport/requests/${r.id}/unassign`);
                                  }
                                  reload();
                                }}
                                className="font-body text-xs px-2 py-1 border" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
                          <option value="">— {locale === "fr" ? "Non affecté" : "Unassigned"}</option>
                          {offers.map((o) => {
                            const fits = (o.remaining_seats || 0) >= (r.nb_people || 1) || o.id === r.assigned_offer_id;
                            const dateMatch = !r.date || !o.date || o.date === r.date;
                            return (
                              <option key={o.id} value={o.id} disabled={!fits || !dateMatch}>
                                {o.driver_name} · {o.origin}→{o.destination} · {o.date} ({o.remaining_seats}/{o.seats})
                              </option>
                            );
                          })}
                        </select>
                        {assigned && <div className="text-xs mt-1" style={{ color: "var(--moss)" }}>✓ {assigned.driver_name}</div>}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <button onClick={async () => { if (window.confirm("Supprimer ?")) { await api.delete(`/admin/transport/requests/${r.id}`); reload(); } }}
                                className="font-caps text-[0.55rem] px-2 py-1 border"
                                style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)" }}>
                          {locale === "fr" ? "SUPPR." : "DEL."}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TransportOfferForm({ onCreated, locale }) {
  const [f, setF] = useState({ driver_name: "", phone: "", email: "", origin: "", destination: "Prieuré saint Léonard, Vains", date: "", seats: 1, notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/transport/offers", { ...f, seats: parseInt(f.seats, 10) || 1 });
    onCreated?.();
  };
  return (
    <form onSubmit={submit} data-testid="transport-offer-form" className="admin-card space-y-3">
      <div className="font-display text-lg">{locale === "fr" ? "Nouveau trajet proposé" : "New ride offer"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input required placeholder={locale === "fr" ? "Conducteur" : "Driver"} value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Téléphone" : "Phone"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-classic" />
        <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-classic" />
        <input required placeholder={locale === "fr" ? "Départ" : "Origin"} value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })} className="input-classic" />
        <input required placeholder={locale === "fr" ? "Destination" : "Destination"} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} className="input-classic" />
        <input required type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="input-classic" />
        <input type="number" min="1" placeholder={locale === "fr" ? "Places" : "Seats"} value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} className="input-classic" />
        <textarea placeholder={locale === "fr" ? "Notes" : "Notes"} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input-classic md:col-span-3" />
      </div>
      <button type="submit" data-testid="transport-offer-submit" className="btn btn-primary" style={{ fontSize: "0.7rem" }}>
        {locale === "fr" ? "Enregistrer" : "Save"}
      </button>
    </form>
  );
}

function TransportRequestForm({ onCreated, locale }) {
  const [f, setF] = useState({ guest_name: "", phone: "", email: "", origin: "", destination: "Prieuré saint Léonard, Vains", date: "", nb_people: 1, notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/admin/transport/requests", { ...f, nb_people: parseInt(f.nb_people, 10) || 1 });
    onCreated?.();
  };
  return (
    <form onSubmit={submit} data-testid="transport-request-form" className="admin-card space-y-3">
      <div className="font-display text-lg">{locale === "fr" ? "Nouvelle demande de transport" : "New transport request"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <input required placeholder={locale === "fr" ? "Demandeur" : "Requester"} value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Téléphone" : "Phone"} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-classic" />
        <input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="input-classic" />
        <input required placeholder={locale === "fr" ? "Ville de départ" : "Origin city"} value={f.origin} onChange={(e) => setF({ ...f, origin: e.target.value })} className="input-classic" />
        <input placeholder={locale === "fr" ? "Destination" : "Destination"} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} className="input-classic" />
        <input required type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="input-classic" />
        <input type="number" min="1" placeholder={locale === "fr" ? "Personnes" : "People"} value={f.nb_people} onChange={(e) => setF({ ...f, nb_people: e.target.value })} className="input-classic" />
        <textarea placeholder={locale === "fr" ? "Notes" : "Notes"} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="input-classic md:col-span-3" />
      </div>
      <button type="submit" data-testid="transport-request-submit" className="btn btn-primary" style={{ fontSize: "0.7rem" }}>
        {locale === "fr" ? "Enregistrer" : "Save"}
      </button>
    </form>
  );
}

// ============================================================ SHARED
function Th({ children, align }) {
  return (
    <th className={`py-2 px-3 font-caps text-[0.6rem] text-${align || "left"}`}
        style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
      {children}
    </th>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border bg-white p-3" style={{ borderColor: "var(--border)" }}>
      <div className="font-caps text-[0.55rem]" style={{ color: "var(--muted)", letterSpacing: "0.22em" }}>{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

// ============================================================ MAIN
export default function LogisticsTab({ locale }) {
  const [sub, setSub] = useState("teams");
  const subtabs = [
    { key: "teams", label: locale === "fr" ? "Équipes" : "Teams" },
    { key: "housing", label: locale === "fr" ? "Hébergements" : "Housing" },
    { key: "transport", label: locale === "fr" ? "Déplacements" : "Transport" },
  ];
  return (
    <div data-testid="logistics-tab" className="space-y-6">
      <div className="flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        {subtabs.map((st) => (
          <button key={st.key}
                  data-testid={`logistics-sub-${st.key}`}
                  onClick={() => setSub(st.key)}
                  className="font-caps text-[0.65rem] px-4 py-2"
                  style={{
                    background: sub === st.key ? "var(--parchment-2)" : "transparent",
                    borderBottom: sub === st.key ? "2px solid var(--cinnabar)" : "2px solid transparent",
                    color: sub === st.key ? "var(--cinnabar)" : "var(--ink-2)",
                    letterSpacing: "0.18em",
                  }}>
            {st.label}
          </button>
        ))}
      </div>
      {sub === "teams" && <TeamsKanban locale={locale} />}
      {sub === "housing" && <HousingPanel locale={locale} />}
      {sub === "transport" && <TransportPanel locale={locale} />}
    </div>
  );
}
